import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import api from "../../../services/api";
import { endpoints } from "../../../services/endpoints";
import { ROLE_OPTIONS } from "../../../utils/roles";

export default function UserEditAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    identification: "",
    phone: "",
    typeUser: "OPERATOR",
    companyId: null,
    active: true,
  });

  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInitial = async () => {
    try {
      setLoading(true);
      const [uRes, cRes] = await Promise.all([
        api.get(endpoints.users.byId(id)),
        api.get(endpoints.companies.list),
      ]);

      const u = uRes?.data?.data ?? uRes?.data ?? {};
      setForm({
        name: u.name || "",
        email: u.email || "",
        identification: u.identification ?? u.dni ?? "",
        phone: u.phone || "",
        typeUser: u.typeUser || "OPERATOR",
        companyId: u.companyId ?? u.company?.id ?? null,
        active: u.active ?? true,
      });

      const rawCompanies = cRes?.data?.data ?? cRes?.data ?? [];
      setCompanies(
        (Array.isArray(rawCompanies) ? rawCompanies : []).map((c) => ({
          value: c.id,
          label: c.name,
        }))
      );
    } catch (err) {
      console.error(err);
      alert("No se pudo cargar el usuario/compañías");
      navigate("/dashboardusersadmin");
    } finally {
      setLoading(false);
    }
  };

  const selectedRole = useMemo(
    () => ROLE_OPTIONS.find((r) => r.value === form.typeUser) || null,
    [form.typeUser]
  );

  const selectedCompany = useMemo(
    () => companies.find((c) => c.value === form.companyId) || null,
    [companies, form.companyId]
  );

  const onInput = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSelectRole = (opt) => {
    setForm((f) => ({ ...f, typeUser: opt ? opt.value : "OPERATOR" }));
  };

  const onSelectCompany = (opt) => {
    setForm((f) => ({ ...f, companyId: opt ? opt.value : null }));
  };

  const onToggleActive = () => {
    setForm((f) => ({ ...f, active: !f.active }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.patch(endpoints.users.byId(id), {
        name: form.name,
        email: form.email,
        identification: form.identification,
        phone: form.phone || null,
        typeUser: form.typeUser,
        companyId: form.companyId,
        active: form.active,
      });
      navigate("/dashboardusersadmin");
    } catch (err) {
      console.error(err);
      alert("No se pudo guardar el usuario");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6">Cargando...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-center mb-6">
        Editar usuario #{id}
      </h1>

      <form
        id="user-edit-form"
        onSubmit={onSubmit}
        className="bg-white p-6 rounded shadow space-y-4 max-w-lg mx-auto"
      >
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block font-medium">Nombre</label>
            <input
              name="name"
              value={form.name}
              onChange={onInput}
              className="border rounded w-full p-2"
              required
            />
          </div>
          <div>
            <label className="block font-medium">Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={onInput}
              className="border rounded w-full p-2"
              required
            />
          </div>
          <div>
            <label className="block font-medium">Identificación</label>
            <input
              name="identification"
              value={form.identification}
              onChange={onInput}
              className="border rounded w-full p-2"
            />
          </div>
          <div>
            <label className="block font-medium">Teléfono</label>
            <input
              name="phone"
              value={form.phone}
              onChange={onInput}
              className="border rounded w-full p-2"
            />
          </div>
          <div>
            <label className="block font-medium">Rol</label>
            <Select
              options={ROLE_OPTIONS}
              value={selectedRole}
              onChange={onSelectRole}
              isClearable={false}
            />
          </div>
          <div>
            <label className="block font-medium">Compañía</label>
            <Select
              options={companies}
              value={selectedCompany}
              onChange={onSelectCompany}
              isClearable
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              id="active"
              type="checkbox"
              checked={form.active}
              onChange={onToggleActive}
              className="h-4 w-4"
            />
            <label htmlFor="active" className="select-none">
              Activo
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded border"
            type="button"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-60"
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}